import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertTriangle,
  Upload,
  Download,
  Eye,
  Trash2,
  Bell,
  Calendar,
  TrendingUp,
  Shield,
  RefreshCw,
  Plus,
  Filter,
  X
} from 'lucide-react';
import { apiClient, endpoints } from '../api';
import DirectKYCUpload from './DirectKYCUpload';
import KYCTimeline from './KYCTimeline';
import './KYCDashboard.css';

const KYCDashboard = ({ vendorId }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all | 7 | 30
  const [vendorInfo, setVendorInfo] = useState(null);

  useEffect(() => {
    if (vendorId) {
      fetchDashboardData();
      fetchDocuments();
      fetchNotifications();
      fetchVendorInfo();
    }
  }, [vendorId]);

  const fetchDashboardData = async () => {
    try {
      const response = await apiClient.get(endpoints.kycDashboard(vendorId));
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Fallback to demo data for development
      setDashboardData({
        summary: {
          total_documents_uploaded: 0,
          verified_documents: 0,
          pending_documents: 0,
          rejected_documents: 0,
          documents_expiring_soon: 0
        },
        completion: {
          completion_percentage: 0,
          completion_status: 'NOT_STARTED',
          completed_docs: 0,
          total_required: 6
        },
        requirements: [
          { document_type: 'pan', display_name: 'PAN Card', description: 'Permanent Account Number', is_required: true, max_file_size_mb: 2 },
          { document_type: 'aadhaar', display_name: 'Aadhaar Card', description: 'Unique identification document', is_required: true, max_file_size_mb: 2 },
          { document_type: 'gst', display_name: 'GST Certificate', description: 'GST registration certificate', is_required: true, max_file_size_mb: 5 },
          { document_type: 'fssai', display_name: 'FSSAI License', description: 'Food safety license', is_required: false, max_file_size_mb: 5 },
          { document_type: 'shopLicense', display_name: 'Shop License', description: 'Trade license', is_required: true, max_file_size_mb: 5 },
          { document_type: 'bankProof', display_name: 'Bank Proof', description: 'Bank account verification', is_required: true, max_file_size_mb: 5 }
        ]
      });
    }
  };

  const fetchVendorInfo = async () => {
    try {
      const response = await apiClient.get(endpoints.kycOverallStatus(vendorId));
      if (response.data?.success && response.data?.data?.vendor) {
        setVendorInfo(response.data.data.vendor);
        return;
      }
    } catch (e) {
      // ignore and fallback to localStorage
    }
    try {
      const stored = localStorage.getItem('vendorData');
      if (stored) {
        const parsed = JSON.parse(stored);
        setVendorInfo(parsed);
      }
    } catch (e) {}
  };

  const fetchDocuments = async () => {
    try {
      const response = await apiClient.get(endpoints.kycDocuments(vendorId));
      if (response.data.success) {
        setDocuments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]); // Fallback to empty array
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get(`${endpoints.kycNotifications(vendorId)}?limit=10`);
      if (response.data.success) {
        setNotifications(response.data.data.notifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]); // Fallback to empty array
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (refreshing) return; // Prevent multiple simultaneous refresh calls
    
    setRefreshing(true);
    setError('');
    
    try {
      await Promise.all([
        fetchDashboardData(),
        fetchDocuments(),
        fetchNotifications()
      ]);
      
      // Show success feedback
      showToast('Dashboard refreshed successfully', 'success');
      
    } catch (error) {
      console.error('Error during refresh:', error);
      setError('Failed to refresh dashboard. Please check your connection and try again.');
      showToast('Failed to refresh dashboard', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.innerHTML = `
      <div class="toast-content">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          ${type === 'success' 
            ? '<polyline points="20,6 9,17 4,12"></polyline>' 
            : '<circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line>'
          }
        </svg>
        <span>${message}</span>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('toast-show');
    });
    
    setTimeout(() => {
      toast.classList.remove('toast-show');
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  };

  const handleDirectUploadComplete = async (data) => {
    try {
      console.log('Upload completed:', data);
      showToast(`${data.successCount} documents uploaded successfully`, 'success');
      await handleRefresh(); // Refresh the dashboard data
    } catch (error) {
      console.error('Error handling upload completion:', error);
      showToast('Upload completed but failed to refresh data', 'error');
    }
  };

  const handleDocumentDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const response = await apiClient.delete(`${endpoints.kycDocumentDelete(documentId)}?vendorId=${vendorId}`);
      if (response.data.success) {
        await fetchDocuments();
        await fetchDashboardData();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const handleDocumentDownload = async (documentId, filename) => {
    try {
      const response = await apiClient.get(`${endpoints.kycDocumentDownload(documentId)}?vendorId=${vendorId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await apiClient.put(endpoints.kycNotificationRead(notificationId), { vendorId });
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'VERIFIED':
      case 'APPROVED':
        return <CheckCircle className="status-icon verified" size={20} />;
      case 'PENDING':
      case 'UPLOADED':
        return <Clock className="status-icon pending" size={20} />;
      case 'REJECTED':
        return <XCircle className="status-icon rejected" size={20} />;
      case 'EXPIRED':
        return <AlertTriangle className="status-icon expired" size={20} />;
      default:
        return <FileText className="status-icon default" size={20} />;
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'VERIFIED': 'status-badge verified',
      'APPROVED': 'status-badge verified',
      'PENDING': 'status-badge pending',
      'UPLOADED': 'status-badge pending',
      'REJECTED': 'status-badge rejected',
      'EXPIRED': 'status-badge expired',
      'MANUAL_REVIEW': 'status-badge review'
    };

    return (
      <span className={statusClasses[status] || 'status-badge default'}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getOverallStatus = () => {
    const status = vendorInfo?.status || dashboardData?.completion?.completion_status || 'PENDING';
    // Normalize a few known values
    if (status === 'APPROVED' || status === 'VERIFIED') return 'VERIFIED';
    if (status === 'REJECTED') return 'REJECTED';
    return 'PENDING';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'HIGH':
      case 'URGENT':
        return <AlertTriangle className="priority-icon high" size={16} />;
      case 'MEDIUM':
        return <Bell className="priority-icon medium" size={16} />;
      default:
        return <Bell className="priority-icon low" size={16} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDocuments = documents
    .filter(doc => {
      if (filterStatus !== 'all') {
        if (!(doc.verification_status === filterStatus || doc.doc_status === filterStatus)) return false;
      }
      return true;
    })
    .filter(doc => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        (doc.display_name || '').toLowerCase().includes(q) ||
        (doc.document_type || '').toLowerCase().includes(q) ||
        (doc.original_name || '').toLowerCase().includes(q)
      );
    })
    .filter(doc => {
      if (dateFilter === 'all') return true;
      const days = parseInt(dateFilter, 10);
      const uploadedAt = doc.uploaded_at ? new Date(doc.uploaded_at) : null;
      if (!uploadedAt) return false;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      return uploadedAt >= cutoff;
    });

  if (loading) {
    return (
      <div className="kyc-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading KYC Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kyc-dashboard-error">
        <XCircle size={48} />
        <h3>Error Loading Dashboard</h3>
        <p>{error}</p>
        <button onClick={handleRefresh} className="retry-btn">
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="kyc-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-title">
            <Shield size={24} />
            <h1>KYC Dashboard</h1>
          </div>
          <div className="header-actions">
            <button 
              onClick={handleRefresh} 
              className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
              disabled={refreshing || loading}
              title="Refresh dashboard data"
            >
              <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <DirectKYCUpload 
              vendorId={vendorId}
              onUploadComplete={handleDirectUploadComplete}
            />
          </div>
        </div>

        <div className="dashboard-tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
          <button 
            className={`tab ${activeTab === 'timeline' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            Timeline
          </button>
          <button 
            className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            Notifications
            {notifications.filter(n => !n.is_read).length > 0 && (
              <span className="notification-badge">
                {notifications.filter(n => !n.is_read).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Vendor Info Header Card */}
      <div className="vendor-info-card">
        <div className="vendor-primary">
          <div className="vendor-ident">
            <div className="vendor-name">{vendorInfo?.name || vendorInfo?.shopName || 'Vendor'}</div>
            {vendorInfo?.email && (
              <div className="vendor-email">{vendorInfo.email}</div>
            )}
          </div>
          <div className="vendor-status">
            {getStatusBadge(getOverallStatus())}
            <div className="vendor-progress">
              <div className="vendor-progress-bar">
                <div
                  className="vendor-progress-fill"
                  style={{ width: `${dashboardData?.completion?.completion_percentage || 0}%` }}
                />
              </div>
              <span className="vendor-progress-text">{dashboardData?.completion?.completion_percentage || 0}%</span>
            </div>
          </div>
        </div>
        <div className="vendor-actions">
          <button className="vendor-btn" onClick={() => setActiveTab('documents')}>
            <FileText size={16} />
            Edit KYC
          </button>
          <button className="vendor-btn" onClick={() => setActiveTab('overview')}>
            <Eye size={16} />
            View Status
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* KYC Progress Overview */}
            <div className="progress-section">
              <h2>KYC Completion Progress</h2>
              <div className="progress-cards">
                <div className="progress-card main">
                  <div className="progress-circle">
                    <div className="circle-progress" style={{
                      background: `conic-gradient(#10b981 ${(dashboardData?.completion?.completion_percentage || 0) * 3.6}deg, #e5e7eb 0deg)`
                    }}>
                      <div className="circle-inner">
                        <span className="percentage">{dashboardData?.completion?.completion_percentage || 0}%</span>
                        <span className="label">Complete</span>
                      </div>
                    </div>
                  </div>
                  <div className="progress-details">
                    <h3>Overall Status: {dashboardData?.completion?.completion_status || 'NOT_STARTED'}</h3>
                    <p>{dashboardData?.completion?.completed_docs || 0} of {dashboardData?.completion?.total_required || 0} required documents verified</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="stats-section">
              <h2>Quick Statistics</h2>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon uploaded">
                    <Upload size={24} />
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">{dashboardData?.summary?.total_documents_uploaded || 0}</span>
                    <span className="stat-label">Documents Uploaded</span>
                  </div>
                </div>

                <div className={`stat-card ${getOverallStatus() === 'VERIFIED' ? 'highlight' : ''}`}>
                  <div className="stat-icon verified">
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">{dashboardData?.summary?.verified_documents || 0}</span>
                    <span className="stat-label">Verified</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon pending">
                    <Clock size={24} />
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">{dashboardData?.summary?.pending_documents || 0}</span>
                    <span className="stat-label">Under Review</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon rejected">
                    <XCircle size={24} />
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">{dashboardData?.summary?.rejected_documents || 0}</span>
                    <span className="stat-label">Rejected</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon expiring">
                    <Calendar size={24} />
                  </div>
                  <div className="stat-content">
                    <span className="stat-number">{dashboardData?.summary?.documents_expiring_soon || 0}</span>
                    <span className="stat-label">Expiring Soon</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Document Requirements */}
            <div className="requirements-section">
              <h2>Document Requirements</h2>
              <div className="requirements-grid">
                {dashboardData?.requirements?.map((req, index) => {
                  const userDoc = documents.find(doc => doc.document_type === req.document_type);
                  return (
                    <div key={index} className={`requirement-card ${userDoc ? 'completed' : 'pending'}`}>
                      <div className="requirement-header">
                        <div className="requirement-info">
                          <h4>{req.display_name}</h4>
                          <p>{req.description}</p>
                        </div>
                        {userDoc ? getStatusIcon(userDoc.verification_status) : <FileText size={20} />}
                      </div>
                      <div className="requirement-details">
                        <div className="requirement-meta">
                          <span className={`required-badge ${req.is_required ? 'required' : 'optional'}`}>
                            {req.is_required ? 'Required' : 'Optional'}
                          </span>
                          <span className="size-limit">Max {req.max_file_size_mb}MB</span>
                        </div>
                        {userDoc && (
                          <div className="document-status">
                            {getStatusBadge(userDoc.verification_status)}
                            <span className="upload-date">
                              Uploaded {formatDate(userDoc.uploaded_at)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="documents-tab">
            <div className="documents-header">
              <h2>Document Management</h2>
              <div className="documents-filters">
                <div className="search-group">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by name, type, or file..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <Filter size={16} />
                  <select 
                    value={filterStatus} 
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Documents</option>
                    <option value="VERIFIED">Verified</option>
                    <option value="PENDING">Pending</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="EXPIRED">Expired</option>
                  </select>
                </div>
                <div className="filter-group">
                  <Calendar size={16} />
                  <select 
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Dates</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Reason Banner */}
            {documents.some(d => d.verification_status === 'REJECTED' && d.verification_notes) && (
              <div className="kyc-reason-banner rejected">
                <AlertTriangle size={18} />
                <div>
                  <div className="reason-title">KYC Rejection Reason</div>
                  <div className="reason-text">{documents.find(d => d.verification_status === 'REJECTED' && d.verification_notes)?.verification_notes}</div>
                </div>
              </div>
            )}

            {/* Desktop Table */}
            <div className="documents-table-wrapper">
              <table className="documents-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>File</th>
                    <th>Size</th>
                    <th>Uploaded</th>
                    <th>Expires</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc, index) => (
                    <tr key={index}>
                      <td>
                        <div className="table-doc-title">
                          <FileText size={16} />
                          <span>{doc.display_name || (doc.document_type || '').toUpperCase()}</span>
                        </div>
                      </td>
                      <td className="truncate">{doc.original_name}</td>
                      <td>{formatFileSize(doc.file_size)}</td>
                      <td>{formatDate(doc.uploaded_at)}</td>
                      <td className={doc.is_expired ? 'expired' : ''}>{doc.expiry_date ? formatDate(doc.expiry_date) : '-'}</td>
                      <td>{getStatusBadge(doc.verification_status)}</td>
                      <td>
                        <div className="table-actions">
                          <button 
                            onClick={() => handleDocumentDownload(doc.id, doc.original_name)}
                            className="action-btn download"
                            title="Download"
                          >
                            <Download size={16} />
                          </button>
                          <button 
                            onClick={() => setSelectedDocument(doc)}
                            className="action-btn view"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button 
                            onClick={() => handleDocumentDelete(doc.id)}
                            className="action-btn delete"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="documents-grid">
              {filteredDocuments.map((doc, index) => (
                <div key={index} className="document-card">
                  <div className="document-header">
                    <div className="document-info">
                      <div className="document-title">
                        <FileText size={20} />
                        <h4>{doc.display_name || doc.document_type.toUpperCase()}</h4>
                      </div>
                      <div className="document-meta">
                        <span className="file-name">{doc.original_name}</span>
                        <span className="file-size">{formatFileSize(doc.file_size)}</span>
                      </div>
                    </div>
                    <div className="document-status-section">
                      {getStatusBadge(doc.verification_status)}
                      {doc.is_expiring_soon && (
                        <span className="expiry-warning">
                          <AlertTriangle size={14} />
                          Expiring Soon
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="document-details">
                    <div className="detail-row">
                      <span className="label">Uploaded:</span>
                      <span className="value">{formatDate(doc.uploaded_at)}</span>
                    </div>
                    {doc.expiry_date && (
                      <div className="detail-row">
                        <span className="label">Expires:</span>
                        <span className={`value ${doc.is_expired ? 'expired' : ''}`}>
                          {formatDate(doc.expiry_date)}
                        </span>
                      </div>
                    )}
                    {doc.verification_checked_at && (
                      <div className="detail-row">
                        <span className="label">Verified:</span>
                        <span className="value">{formatDate(doc.verification_checked_at)}</span>
                      </div>
                    )}
                    {doc.verification_notes && (
                      <div className="detail-row">
                        <span className="label">Notes:</span>
                        <span className="value notes">{doc.verification_notes}</span>
                      </div>
                    )}
                  </div>

                  <div className="document-actions">
                    <button 
                      onClick={() => handleDocumentDownload(doc.id, doc.original_name)}
                      className="action-btn download"
                      title="Download"
                    >
                      <Download size={16} />
                    </button>
                    <button 
                      onClick={() => setSelectedDocument(doc)}
                      className="action-btn view"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      onClick={() => handleDocumentDelete(doc.id)}
                      className="action-btn delete"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {doc.history && doc.history.length > 0 && (
                    <div className="document-history">
                      <h5>Recent Activity</h5>
                      <div className="history-list">
                        {doc.history.slice(0, 3).map((hist, idx) => (
                          <div key={idx} className="history-item">
                            <span className="history-action">{hist.action}</span>
                            <span className="history-date">{formatDate(hist.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {filteredDocuments.length === 0 && (
              <div className="empty-state">
                <FileText size={48} />
                <h3>No Documents Found</h3>
                <p>
                  {filterStatus === 'all' 
                    ? 'Upload your first document to get started with KYC verification.'
                    : `No documents found with status: ${filterStatus}`
                  }
                </p>
                <DirectKYCUpload 
                  vendorId={vendorId}
                  onUploadComplete={handleDirectUploadComplete}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (
          <KYCTimeline vendorId={vendorId} />
        )}

        {activeTab === 'notifications' && (
          <div className="notifications-tab">
            <h2>Notifications</h2>
            <div className="notifications-list">
              {notifications.map((notification, index) => (
                <div 
                  key={index} 
                  className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                  onClick={() => !notification.is_read && markNotificationAsRead(notification.id)}
                >
                  <div className="notification-icon">
                    {getPriorityIcon(notification.priority)}
                  </div>
                  <div className="notification-content">
                    <h4>{notification.title}</h4>
                    <p>{notification.message}</p>
                    <span className="notification-date">{formatDate(notification.created_at)}</span>
                  </div>
                  {!notification.is_read && <div className="unread-indicator"></div>}
                </div>
              ))}
            </div>

            {notifications.length === 0 && (
              <div className="empty-state">
                <Bell size={48} />
                <h3>No Notifications</h3>
                <p>You're all caught up! New notifications will appear here.</p>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default KYCDashboard;

